import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { coachingAreas, faqs } from '../data/content';
import { SectionHeading, Notice } from '../components/Shared';
import { submitRecord } from '../lib/api';

export function Coaching() {
  return (
    <PageHero title="Professional growth, guided by better conversations" text="Use focused reflection to clarify challenges, explore options and turn insights into practical action.">
      <div className="area-grid">{coachingAreas.map((area) => <article key={area}><h3>{area}</h3><p>Structured reflection and practical next steps for {area.toLowerCase()}.</p></article>)}</div>
    </PageHero>
  );
}

export function HowItWorks() {
  const stages = [
    ['Choose your subscription', 'Select flexible monthly access or save with an annual plan.'],
    ['Create your account and pay', 'Use secure Stripe checkout. Card information is never stored by us.'],
    ['Receive coaching access', 'Your account enters allocation and you receive an activation email when ready.'],
    ['Start coaching', 'Open your AI Coach, choose a focus and build a regular development rhythm.']
  ];
  return <PageHero title="A simple path to continuous growth" text="Start securely in four straightforward stages."><div className="process-list">{stages.map(([title, text], index) => <article key={title}><b>{index + 1}</b><div><h3>{title}</h3><p>{text}</p></div></article>)}</div></PageHero>;
}

export function Organisations() {
  return (
    <PageHero title="Scalable coaching for modern organisations" text="Support employee development, leadership programmes and workplace learning with flexible licences and centralised billing.">
      <div className="grid three">{['Leadership development', 'Graduate programmes', 'Talent development', 'Management coaching', 'Career development', 'Employee engagement'].map((item) => <article className="card" key={item}><h3>{item}</h3><p>Accessible development experiences designed around your programme objectives.</p></article>)}</div>
      <LeadForm />
    </PageHero>
  );
}

function LeadForm() {
  const [state, setState] = useState({ status: '', message: '' });
  const submit = async (event) => {
    event.preventDefault();
    setState({ status: 'loading', message: '' });
    const data = Object.fromEntries(new FormData(event.currentTarget));
    try {
      await submitRecord('corporate_leads', data);
      event.currentTarget.reset();
      setState({ status: 'success', message: 'Thank you. Your proposal request has been received.' });
    } catch (error) {
      setState({ status: 'error', message: error.message });
    }
  };
  return (
    <section className="form-panel">
      <SectionHeading eyebrow="Talk to our team" title="Request a corporate proposal" />
      <form className="form-grid" onSubmit={submit}>
        {['organisation_name', 'contact_name', 'job_title', 'work_email', 'telephone', 'country', 'estimated_users'].map((name) => (
          <label key={name}>{name.replaceAll('_', ' ')}<input name={name} type={name.includes('email') ? 'email' : 'text'} required /></label>
        ))}
        <label className="wide">Programme objective<input name="programme_objective" required /></label>
        <label className="wide">Message<textarea name="message" rows="4" required /></label>
        <button className="button wide" disabled={state.status === 'loading'}>Request a Proposal</button>
        {state.message && <Notice type={state.status}>{state.message}</Notice>}
      </form>
    </section>
  );
}

export function About() {
  return <PageHero title="Development technology with a human purpose" text="We make structured reflection and professional development more accessible."><div className="prose"><h2>Our Purpose</h2><p>We help people create space for intentional learning, better habits and thoughtful action.</p><h2>Our Approach</h2><p>Our service combines accessible AI coaching technology with responsible commercial support and clear boundaries.</p><h2>Who We Serve</h2><p>Individuals, professionals, organisations, universities, coaches and leadership programmes.</p><h2>Our Commitment to Privacy</h2><p>We seek to collect only necessary commercial account information and keep it separate from coaching conversation data where possible.</p></div></PageHero>;
}

export function Resources() {
  return <PageHero title="Ideas for continuous growth" text="Practical resources for leaders, professionals and learning teams."><div className="grid three">{['Leadership', 'Career Development', 'Personal Growth', 'AI Coaching', 'Productivity', 'Employee Development'].map((category) => <article className="card resource" key={category}><span>Resource category</span><h3>{category}</h3><p>Future evidence-informed articles and practical development guides.</p><Link to="/contact">Suggest a topic</Link></article>)}</div></PageHero>;
}

export function FaqBlock() {
  return <div className="faq-list">{faqs.map(([q, a]) => <details key={q}><summary>{q}</summary><p>{a}</p></details>)}</div>;
}

function PageHero({ title, text, children }) {
  return <><section className="page-hero"><span className="eyebrow">Continuous development</span><h1>{title}</h1><p>{text}</p></section><section className="section">{children}</section></>;
}